import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Contract, Contractor } from '../services/api';
import './ContractPicker.css';

export const ACTIVE_CONTRACT_STATUS = 'в работе';

export interface ContractPickerProps {
  contracts: Contract[];
  contractorsById: Map<number, Contractor>;
  value: number | null;
  onChange: (id: number) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
}

export const ContractPicker: React.FC<ContractPickerProps> = ({
  contracts,
  contractorsById,
  value,
  onChange,
  disabled,
  placeholder = 'Введите номер договора или название контрагента',
  emptyLabel = 'Выберите договор',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => contracts.find((c) => c.id === value) ?? null, [contracts, value]);

  const selectedLabel = useMemo(() => {
    if (!selected) return '';
    const contractor = contractorsById.get(selected.contractor_id);
    return contractor ? `№ ${selected.contract_number} — ${contractor.name}` : `№ ${selected.contract_number}`;
  }, [selected, contractorsById]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<number, { contractor: Contractor | undefined; contracts: Contract[] }>();
    for (const c of contracts) {
      const contractor = contractorsById.get(c.contractor_id);
      const number = c.contract_number.toLowerCase();
      const name = (contractor?.name ?? '').toLowerCase();
      const matches = q === '' || number.includes(q) || name.includes(q);
      if (!matches) continue;
      const existing = map.get(c.contractor_id);
      if (existing) {
        existing.contracts.push(c);
      } else {
        map.set(c.contractor_id, { contractor, contracts: [c] });
      }
    }
    const arr = Array.from(map.entries()).map(([id, group]) => ({
      id,
      contractor: group.contractor,
      contracts: group.contracts.sort((a, b) =>
        a.contract_number.localeCompare(b.contract_number, 'ru', { numeric: true, sensitivity: 'base' }),
      ),
    }));
    arr.sort((a, b) => (a.contractor?.name ?? '').localeCompare(b.contractor?.name ?? '', 'ru'));
    return arr;
  }, [contracts, contractorsById, query]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const openPicker = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div
      className={`contract-picker${disabled ? ' contract-picker--disabled' : ''}`}
      ref={containerRef}
    >
      {open ? (
        <input
          ref={inputRef}
          type="text"
          className="contract-picker__field"
          placeholder={selectedLabel || placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          aria-label="Поиск договора"
        />
      ) : (
        <button
          type="button"
          className="contract-picker__trigger"
          onClick={openPicker}
          disabled={disabled}
        >
          <span className="contract-picker__text">{selectedLabel || emptyLabel}</span>
          <span className="contract-picker__caret" aria-hidden>
            ▾
          </span>
        </button>
      )}
      {open && (
        <div className="contract-picker__popup" role="listbox">
          {groups.length === 0 ? (
            <div className="contract-picker__empty">Ничего не найдено</div>
          ) : (
            groups.map((group) => (
              <div key={group.id} className="contract-picker__group">
                <div className="contract-picker__group-title">
                  {group.contractor?.name ?? '— контрагент не найден —'}
                </div>
                {group.contracts.map((c) => {
                  const isActive = c.id === value;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      role="option"
                      aria-selected={isActive}
                      className={`contract-picker__option${isActive ? ' contract-picker__option--active' : ''}`}
                      onClick={() => {
                        onChange(c.id);
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      <span className="contract-picker__option-num">№ {c.contract_number}</span>
                      {c.contract_status !== ACTIVE_CONTRACT_STATUS ? (
                        <span className="contract-picker__option-meta">{c.contract_status}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ContractPicker;
